// KbLink — prefix-aware knowledge-base link helper.
//
// Problem this solves: the KB pages are mounted at BOTH /knowledge (guest)
// and /app/knowledge (authed). An absolute path like
// `<Link to="/knowledge/leaf-potency-math">` works from the guest tree but
// silently bounces authed users to /app via the catch-all in routes.tsx.
//
// KbLink reads the current pathname once via useLocation() and resolves the
// correct prefix at render time. Callers just pass a slug (or omit it for
// the KB index).
//
// For "open in a new tab" semantics (Learn chat citations, Buddy chat
// citation chips), pass `newTab` — the component renders an `<a
// target="_blank">` with the resolved href so the prefix is still right.
//
// resolveKbPath is exported as a pure function so unit tests don't need a
// DOM or a router — same convention as src/lib/useToday's msUntilNextMidnight.

import { Link, useLocation } from 'react-router-dom';
import type { CSSProperties, ReactNode } from 'react';

/**
 * Pure: given the current pathname and an optional slug, return the
 * correctly-prefixed KB path. `/app/*` → `/app/knowledge/...`; anything
 * else → `/knowledge/...`.
 */
export function resolveKbPath(currentPath: string, slug?: string): string {
  // Tight match: "/app" exactly or "/app/..."; reject "/apple" or
  // "/something/app/..." even though they share a prefix.
  const isAuthed = currentPath === '/app' || currentPath.startsWith('/app/');
  const prefix = isAuthed ? '/app/knowledge' : '/knowledge';
  return slug ? `${prefix}/${slug}` : prefix;
}

interface KbLinkProps {
  /** KB entry slug. Omit for the KB index. */
  slug?: string;
  children: ReactNode;
  className?: string;
  /**
   * Open in a new tab — renders as `<a target="_blank">` rather than a
   * react-router `<Link>`. Use this in chat/AI surfaces where clicking a
   * link should NOT replace the active screen.
   */
  newTab?: boolean;
  title?: string;
  style?: CSSProperties;
  'aria-label'?: string;
  /** Optional click handler — passed through to whichever element renders. */
  onClick?: () => void;
}

export function KbLink({
  slug,
  children,
  className,
  newTab,
  title,
  style,
  onClick,
  ...rest
}: KbLinkProps) {
  const { pathname } = useLocation();
  const to = resolveKbPath(pathname, slug);
  const ariaLabel = rest['aria-label'];
  if (newTab) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        title={title}
        style={style}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }
  return (
    <Link
      to={to}
      className={className}
      title={title}
      style={style}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}
